import { useState } from "react";
import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Progress,
  Text,
} from "@radix-ui/themes";
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  Database,
  FileText,
  Workflow,
  Sparkles,
  X,
} from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";

interface OnboardingChecklistProps {
  entityCount: number;
  workflowCount: number;
  onDismiss: () => void;
}

export function OnboardingChecklist({
  entityCount,
  workflowCount,
  onDismiss,
}: OnboardingChecklistProps) {
  const { safeNavigate } = useAuth();
  const [activeStep, setActiveStep] = useState<number | null>(null);

  const steps = [
    {
      id: 1,
      title: "Configure Your Data Model",
      description:
        "Define your entities (e.g. Users, Products) and fields. This automatically scaffolds your database schemas and APIs.",
      icon: <Database size={18} />,
      isCompleted: entityCount > 0,
      route: "/settings/data-model",
      actionText: "Configure Data Model",
    },
    {
      id: 2,
      title: "Create Automated Workflows",
      description:
        "Set up automation triggers and actions (e.g. sending emails or updating records) when data changes in your entities.",
      icon: <Workflow size={18} />,
      isCompleted: workflowCount > 0,
      route: "/workflows",
      actionText: "Create Workflow",
    },
    {
      id: 3,
      title: "Explore API Documentation",
      description:
        "View and test your generated APIs interactively using Swagger. Instantly check endpoints for your custom entities.",
      icon: <FileText size={18} />,
      isCompleted:
        entityCount > 0 &&
        localStorage.getItem("anythink_visited_docs") === "true",
      route: "/api-docs",
      actionText: "Explore API Docs",
      isDisabled: entityCount === 0,
    },
  ];

  const completedCount = steps.filter((step) => step.isCompleted).length;
  const progressPercent = Math.round((completedCount / steps.length) * 100);

  const handleStepClick = (step: (typeof steps)[0]) => {
    if (step.isDisabled) return;
    if (step.id === 3) {
      localStorage.setItem("anythink_visited_docs", "true");
    }
    safeNavigate(step.route);
  };

  return (
    <Card
      style={{
        width: "100%",
        padding: "var(--space-5)",
        marginBottom: "var(--space-5)",
        background:
          "linear-gradient(135deg, var(--gray-2) 0%, var(--gray-3) 100%)",
        border: "1px solid var(--gray-5)",
        borderRadius: "var(--radius-4)",
        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative gradient overlay */}
      <Box
        style={{
          position: "absolute",
          top: "-50px",
          right: "-50px",
          width: "200px",
          height: "200px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, var(--accent-a3) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <Flex justify="between" align="start" mb="4">
        <Flex gap="3" align="center">
          <Sparkles style={{ color: "var(--accent-9)" }} size={24} />
          <Box>
            <Heading
              size="4"
              weight="bold"
              style={{ letterSpacing: "-0.02em" }}
            >
              Welcome to Anythink!
            </Heading>
            <Text size="2" color="gray">
              Let&apos;s get your workspace set up in just a few quick steps.
            </Text>
          </Box>
        </Flex>

        <Button
          variant="ghost"
          color="gray"
          onClick={onDismiss}
          style={{ cursor: "pointer", padding: "var(--space-1)" }}
          aria-label="Dismiss onboarding"
        >
          <X size={16} />
        </Button>
      </Flex>

      {/* Progress Bar */}
      <Box mb="5">
        <Flex justify="between" align="center" mb="2">
          <Text size="2" weight="medium" color="gray">
            Setup Progress
          </Text>
          <Text size="2" weight="bold" style={{ color: "var(--accent-9)" }}>
            {completedCount} of {steps.length} steps completed (
            {progressPercent}%)
          </Text>
        </Flex>
        <Progress
          value={progressPercent}
          size="2"
          style={{ transition: "all 0.5s ease" }}
        />
      </Box>

      {/* Checklist Grid */}
      <Flex direction="column" gap="3">
        {steps.map((step) => {
          const isHovered = activeStep === step.id;
          return (
            <Box
              key={step.id}
              onMouseEnter={() => setActiveStep(step.id)}
              onMouseLeave={() => setActiveStep(null)}
              style={{
                display: "flex",
                flexDirection: "column",
                padding: "var(--space-3) var(--space-4)",
                borderRadius: "var(--radius-3)",
                backgroundColor: step.isDisabled
                  ? "transparent"
                  : isHovered
                    ? "var(--gray-3)"
                    : "var(--gray-2)",
                border: `1px solid ${
                  isHovered ? "var(--gray-6)" : "var(--gray-4)"
                }`,
                opacity: step.isDisabled ? 0.5 : 1,
                transition: "all 0.2s ease-in-out",
                transform: isHovered ? "translateY(-1px)" : "translateY(0)",
              }}
            >
              <Flex align="center" justify="between" gap="4" wrap="wrap">
                <Flex
                  align="start"
                  gap="3"
                  style={{ flex: 1, minWidth: "250px" }}
                >
                  <Box style={{ paddingTop: "2px" }}>
                    {step.isCompleted ? (
                      <CheckCircle2
                        size={20}
                        style={{ color: "var(--green-9)" }}
                      />
                    ) : (
                      <Circle size={20} style={{ color: "var(--gray-8)" }} />
                    )}
                  </Box>
                  <Box>
                    <Flex align="center" gap="2">
                      <Box
                        style={{
                          color: step.isCompleted
                            ? "var(--green-9)"
                            : "var(--gray-11)",
                        }}
                      >
                        {step.icon}
                      </Box>
                      <Text
                        size="3"
                        weight="bold"
                        style={{
                          textDecoration: step.isCompleted
                            ? "line-through"
                            : "none",
                          color: step.isCompleted
                            ? "var(--gray-9)"
                            : "var(--gray-12)",
                        }}
                      >
                        {step.title}
                      </Text>
                    </Flex>
                    <Text
                      size="2"
                      color="gray"
                      style={{ display: "block", marginTop: "var(--space-1)" }}
                    >
                      {step.description}
                    </Text>
                  </Box>
                </Flex>

                <Button
                  size="2"
                  variant={step.isCompleted ? "soft" : "solid"}
                  disabled={step.isDisabled}
                  onClick={() => handleStepClick(step)}
                  style={{
                    cursor: step.isDisabled ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  {step.actionText}
                  <ArrowRight size={14} />
                </Button>
              </Flex>
            </Box>
          );
        })}
      </Flex>
    </Card>
  );
}
